import { ApiClient } from './apiClient';
import type { TerminalAlert } from './adminService';

// KAN-105: Clarus (Aavora) Starlink terminals.
//
// Inventory reads come from our own database via the hourly sync, so they are
// fast and filterable. Telemetry, alerts and usage are proxied live because
// they are per-device and time-windowed — see the clarus lambda.

export interface TerminalAllocation {
  assignmentId: number;
  organizationId: number;
  orgName: string | null;
  assetId: number | null;
  assetName: string | null;
  userId: number | null;
  userName: string | null;
  friendlyName: string | null;
}

export interface Terminal {
  deviceId: string;
  serviceLineNumber: string | null;
  clientName: string | null;
  siteName: string | null;
  kitSerialNumber: string | null;
  dishSerialNumber: string | null;
  isActive: boolean;
  isOnline: boolean;
  runningSoftwareVersion: string | null;
  lastTelemetryAt: string | null;
  signalQuality: number | null;
  pingLatencyMsAvg: number | null;
  pingDropRateAvg: number | null;
  obstructionPercentTime: number | null;
  uptimeSeconds: number | null;
  downlinkThroughput: number | null;
  uplinkThroughput: number | null;
  activeAlerts: TerminalAlert[];
  deactivatedAt: string | null;
  lastSyncedAt: string | null;
  allocation: TerminalAllocation | null;
}

export interface ServiceLineLocation {
  formattedAddress: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface ServiceLineSubscription {
  description: string | null;
  activationDate: string | null;
  deactivationDate: string | null;
}

export interface ServiceLine {
  serviceLineNumber: string;
  clientName: string | null;
  siteName: string | null;
  nickname: string | null;
  isActive: boolean | null;
  location: ServiceLineLocation;
  subscription: ServiceLineSubscription;
  terminalCount?: number;
  lastSyncedAt?: string | null;
}

// Live telemetry as returned by Clarus. Numeric throughput fields arrive as
// strings from the upstream API, so both forms are accepted.
export interface LiveTelemetry {
  timestamp: string;
  deviceId: string;
  serviceLineNumber: string | null;
  siteName: string | null;
  clientName: string | null;
  pingDropRateAvg: number | null;
  obstructionPercentTime: number | null;
  signalQuality: number | null;
  pingLatencyMsAvg: number | null;
  uptime: number | null;
  downlinkThroughput: number | string | null;
  uplinkThroughput: number | string | null;
  runningSoftwareVersion: string | null;
  activeAlerts: TerminalAlert[];
  user_terminal_active?: boolean;
  user_terminal_deactivated_date?: string | null;
}

export interface TelemetryHistoryPoint {
  timestamp: string;
  pingDropRateAvg: number | null;
  obstructionPercentTime: number | null;
  signalQuality: number | null;
  pingLatencyMsAvg: number | null;
  uptime: number | null;
  downlinkThroughput: number | string | null;
  uplinkThroughput: number | string | null;
  runningSoftwareVersion: string | null;
  activeAlerts: TerminalAlert[];
}

export interface DeviceAlertEntry {
  deviceId: string;
  siteName: string | null;
  softwareUpdatePending: boolean;
  alerts: TerminalAlert[];
  user_terminal_active?: boolean;
  user_terminal_deactivated_date?: string | null;
}

export interface DailyDataUsage {
  date: string;
  priorityGB: number;
  optInPriorityGB: number;
  standardGB: number;
  nonBillableGB: number;
}

export interface BillingCycle {
  startDate: string;
  endDate: string;
  totalPriorityGB: number;
  totalStandardGB: number;
  totalOptInPriorityGB: number;
  totalNonBillableGB: number;
  dailyDataUsage: DailyDataUsage[];
}

export interface TerminalUsage {
  serviceLineNumber: string;
  clientName: string | null;
  billingPeriod: string;
  startDate: string;
  endDate: string;
  lastUpdated: string;
  elapsedDays: number;
  remainingDays: number;
  subscriptionPackageName: string | null;
  subscriptionAllowance: number | null;
  totalUsage: number | null;
  remainingAllowance: number | null;
  avgDailyUsage: number | null;
  forecastUsage: number | null;
  forecastToExceedAllowance: boolean;
  allowanceConsumedDate: string | null;
  billingCycles: BillingCycle[];
  /**
   * Present on the Clarus payload and passed straight through by the API.
   * `isOptedIntoOverage` is the auto top-up flag (KAN-111): data beyond the
   * allowance is billed automatically rather than blocked.
   */
  servicePlan?: {
    productId: string | null;
    isoCurrencyCode: string | null;
    isMobilePlan: boolean | null;
    isOptedIntoOverage: boolean | null;
    activeFrom: string | null;
  } | null;
}

export interface TerminalDetailResponse {
  terminal: Terminal;
  serviceLine: ServiceLine | null;
}

export interface SyncRun {
  id: number;
  sync_type: string;
  status: 'success' | 'partial' | 'failed';
  terminals_seen: number;
  terminals_upserted: number;
  service_lines_upserted: number;
  error_message: string | null;
  duration_ms: number | null;
  created_at: string;
}

export const getTerminals = async (
  params?: { search?: string; online?: boolean; status?: 'active' | 'inactive'; limit?: number },
  signal?: AbortSignal
): Promise<Terminal[]> => {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.online !== undefined) query.set('online', String(params.online));
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  const res = await ApiClient.get<{ terminals: Terminal[]; total: number }>(
    `/clarus/terminals${qs ? '?' + qs : ''}`,
    signal
  );
  return res.terminals;
};

export const getTerminal = (
  deviceId: string,
  signal?: AbortSignal
): Promise<TerminalDetailResponse> =>
  ApiClient.get<TerminalDetailResponse>(
    `/clarus/terminals/${encodeURIComponent(deviceId)}`,
    signal
  );

export const getTerminalTelemetry = (
  deviceId: string,
  signal?: AbortSignal
): Promise<{ telemetry: LiveTelemetry | null }> =>
  ApiClient.get(`/clarus/terminals/${encodeURIComponent(deviceId)}/telemetry`, signal);

export const getTerminalHistory = (
  deviceId: string,
  signal?: AbortSignal
): Promise<{ points: TelemetryHistoryPoint[]; metadata: Record<string, unknown> | null }> =>
  ApiClient.get(`/clarus/terminals/${encodeURIComponent(deviceId)}/telemetry/history`, signal);

export const getTerminalAlerts = (
  deviceId: string,
  signal?: AbortSignal
): Promise<{ devices: DeviceAlertEntry[]; metadata: Record<string, unknown> | null }> =>
  ApiClient.get(`/clarus/terminals/${encodeURIComponent(deviceId)}/alerts`, signal);

export const getTerminalUsage = (
  serviceLineNumber: string,
  billingPeriod?: string,
  signal?: AbortSignal
): Promise<{ usage: TerminalUsage }> =>
  ApiClient.get(
    `/clarus/usage/${encodeURIComponent(serviceLineNumber)}${billingPeriod ? `?billingPeriod=${billingPeriod}` : ''}`,
    signal
  );

export const getServiceLines = (signal?: AbortSignal): Promise<{ serviceLines: ServiceLine[] }> =>
  ApiClient.get('/clarus/service-lines', signal);

export const triggerClarusSync = (): Promise<{
  message: string;
  status: string;
  terminalsUpserted: number;
  serviceLinesUpserted: number;
}> => ApiClient.post('/clarus/sync', {});

export const getClarusSyncHistory = (signal?: AbortSignal): Promise<{ runs: SyncRun[] }> =>
  ApiClient.get('/clarus/sync-history', signal);
